import { Component, signal, inject } from '@angular/core';

import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MediaLibraryService } from '../../../../shared/services/media-library.service';
import { CmUiStandardButtonComponent } from '@cm/ui-modules';
import { MediaAsset } from '../../media-library.component';
import { MedVideoHighlight, MedVideoResult } from '../../../../shared/models/medvideo.models';

export interface VideoClipManagerData {
  asset: MediaAsset;
  result: MedVideoResult;
}

@Component({
  selector: 'app-video-clip-manager',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatTabsModule,
    TranslateModule,
    CmUiStandardButtonComponent,
  ],
  templateUrl: './video-clip-manager.component.html',
  styleUrls: ['./video-clip-manager.component.scss'],
})
export class VideoClipManagerComponent {
  dialogRef = inject<MatDialogRef<VideoClipManagerComponent>>(MatDialogRef);
  data = inject<VideoClipManagerData>(MAT_DIALOG_DATA);
  private mediaLibraryService = inject(MediaLibraryService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  selectedClipIndices = signal<Set<number>>(new Set());
  saving = signal(false);
  copiedPlatform = signal<string | null>(null);

  isSelected(clipIndex: number): boolean {
    return this.selectedClipIndices().has(clipIndex);
  }

  toggleClip(clipIndex: number): void {
    this.selectedClipIndices.update((set) => {
      const next = new Set(set);
      if (next.has(clipIndex)) {
        next.delete(clipIndex);
      } else {
        next.add(clipIndex);
      }
      return next;
    });
  }

  get selectedCount(): number {
    return this.selectedClipIndices().size;
  }

  mediaUrl(serverPath: string, videoHash = true): string {
    return '/ai/' + serverPath + (videoHash ? '#00.01' : '');
  }

  copyToClipboard(text: string, platform: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedPlatform.set(platform);
      setTimeout(() => this.copiedPlatform.set(null), 2000);
    });
  }

  async saveToMediaLibrary(): Promise<void> {
    if (this.saving()) return;

    const selectedIndices = this.selectedClipIndices();
    const selectedClips = this.data.result.highlights.filter((h) =>
      selectedIndices.has(h.clip_index),
    );

    if (selectedClips.length === 0) return;

    this.saving.set(true);

    try {
      await this.mediaLibraryService
        .saveClipsToMediaLibrary(
          this.mediaLibraryService.getTenantName(),
          this.data.asset.uuid,
          selectedClips.map((c) => ({
            clip_file: c.clip_file,
            clip_index: c.clip_index,
            topic: c.topic,
            ...(c.medical_reason ? { description: c.medical_reason } : {}),
            ...(c.social?.social_post ? { social: JSON.stringify(c.social.social_post) } : {}),
          })),
        )
        .toPromise();

      this.snackBar.open(this.translate.instant('ADMIN.medialibrary.videoClips.savedSuccess'), '', {
        duration: 3000,
      });

      this.dialogRef.close({ saved: true });
    } catch (error) {
      console.error('[VideoClipManager] Error saving clips:', error);
      this.snackBar.open(this.translate.instant('ADMIN.medialibrary.videoClips.savedError'), '', {
        duration: 3000,
      });
    } finally {
      this.saving.set(false);
    }
  }

  getHighlightDuration(h: MedVideoHighlight): string {
    return `${h.start} → ${h.end}`;
  }
}
